import { BehaviorSubject } from 'rxjs';
import { MetadataPanelVm, MetadataPanelLabels, QualityScoreVm, ValidationStatus } from './metadata-panel.model';
import { ClassificationResult } from '@jump-tech-frontend/domain';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { adviceTexts, mismatchOf, statusOf } from '@jump-tech-frontend/image-analysis';

interface ShownEquipment {
  equipmentType: string | undefined;
  confidence: number;
}

export class MetadataPanelPresenter {
  vm: MetadataPanelVm;
  subject$: BehaviorSubject<MetadataPanelVm>;
  private i18ns: I18nKeys;

  setI18ns(i18ns: I18nKeys): void {
    this.i18ns = i18ns;
  }

  load(vmSubject$: BehaviorSubject<MetadataPanelVm>, result: ClassificationResult | null): void {
    this.subject$ = vmSubject$;

    if (!result) {
      this.subject$.next(null);
      return;
    }

    const status = statusOf(result);
    const shown = this.shownEquipment(result);
    const qualityScorePercent = Math.round((result.qualityScore ?? 0) * 100);

    this.vm = {
      // Status
      status,
      statusTitle: this.getStatusTitle(status),
      statusMessage: adviceTexts(result, 'label', this.i18ns?.aiValidation).join('; '),
      statusColor: this.getStatusColor(status),
      statusIcon: this.getStatusIcon(status),

      // Quality
      qualityScore: qualityScorePercent,
      qualityScoreDisplay: this.formatPercentage(qualityScorePercent, 0),
      qualityScoreColorClass: this.getScoreColorClass(qualityScorePercent),
      qualityAcceptable: result.qualityAcceptable ?? false,
      qualityAcceptableDisplay: this.formatPassFail(result.qualityAcceptable ?? false),
      qualityAcceptableColorClass: this.getPassFailColorClass(result.qualityAcceptable ?? false),
      qualityDetails: this.buildQualityDetails(result),

      // Detection
      showDetection: result.analysisScope !== 'quality',
      equipmentType: this.formatEquipmentType(shown.equipmentType),
      confidence: shown.confidence,
      confidenceDisplay: this.formatPercentage(shown.confidence, 1),
      confidenceColorClass: this.getScoreColorClass(shown.confidence),
      confidenceThresholdMet: result.confidenceThresholdMet ?? false,
      confidenceThresholdMetDisplay: this.formatPassFail(result.confidenceThresholdMet ?? false),
      confidenceThresholdMetColorClass: this.getPassFailColorClass(result.confidenceThresholdMet ?? false),
      fuseCutoutConfidence: result.fuseCutoutConfidence ?? 0,
      consumerUnitConfidence: result.consumerUnitConfidence ?? 0,
      boundingBoxCount: result.boundingBoxes?.length ?? 0,
      boundingBoxLabels: this.extractBoundingBoxLabels(result),
      hasDetections: this.hasValidDetection(result),

      // Dimensions
      imageWidth: result.imageWidth ?? 0,
      imageHeight: result.imageHeight ?? 0,
      sourceWidth: result.sourceWidth ?? 0,
      sourceHeight: result.sourceHeight ?? 0,
      showDimensions: !!(result.imageWidth || result.sourceWidth),

      // Labels
      labels: this.buildLabels()
    };

    this.subject$.next(this.vm);
  }

  private buildLabels(): MetadataPanelLabels {
    const aiI18n = this.i18ns?.aiValidation;

    return {
      quality: aiI18n?.quality || 'Quality',
      detection: aiI18n?.detection || 'Detection',
      warnings: aiI18n?.warnings || 'Warnings',
      dimensions: aiI18n?.dimensions || 'Dimensions',
      overallScore: aiI18n?.overallScore || 'Overall Score',
      issues: aiI18n?.issues || 'Issues',
      equipmentType: aiI18n?.equipmentType || 'Equipment Type',
      confidence: aiI18n?.confidence || 'Confidence',
      boundingBoxes: aiI18n?.boundingBoxes || 'Bounding Boxes',
      show: aiI18n?.show || 'Show',
      hide: aiI18n?.hide || 'Hide',
      showBoundingBoxes: aiI18n?.showBoundingBoxes || 'Show bounding boxes',
      hideBoundingBoxes: aiI18n?.hideBoundingBoxes || 'Hide bounding boxes',
      imageSize: aiI18n?.imageSize || 'Image Size',
      sourceSize: aiI18n?.sourceSize || 'Source Size'
    };
  }

  private getStatusTitle(status: ValidationStatus): string {
    const aiI18n = this.i18ns?.aiValidation;

    const titles: Record<ValidationStatus, string> = {
      success: aiI18n?.validationSuccessful || 'Validation Successful',
      quality_issues: aiI18n?.qualityIssues || 'Quality Issues',
      low_confidence: aiI18n?.lowConfidence || 'Low Confidence',
      wrong_equipment: aiI18n?.wrongEquipment || 'Wrong Equipment',
      no_detection: aiI18n?.noDetection || 'No Detection',
      in_progress: aiI18n?.validating || 'Validating...',
      error: aiI18n?.validationFailed || 'Validation Failed'
    };
    return titles[status];
  }

  private getStatusColor(status: ValidationStatus): 'success' | 'warning' | 'danger' | 'info' | 'low' {
    const colors: Record<ValidationStatus, 'success' | 'warning' | 'danger' | 'info' | 'low'> = {
      success: 'success',
      quality_issues: 'warning',
      low_confidence: 'warning',
      wrong_equipment: 'warning',
      no_detection: 'info',
      in_progress: 'low',
      error: 'danger'
    };
    return colors[status];
  }

  private getStatusIcon(status: ValidationStatus): string {
    const icons: Record<ValidationStatus, string> = {
      success: 'check',
      quality_issues: 'warning',
      low_confidence: 'info',
      wrong_equipment: 'swap_horiz',
      no_detection: 'search_off',
      in_progress: 'hourglass_empty',
      error: 'error'
    };
    return icons[status];
  }

  private shownEquipment(result: ClassificationResult): ShownEquipment {
    const mismatch = mismatchOf(result);
    const detectedEquipment = mismatch?.params['detectedEquipment'];
    if (typeof detectedEquipment !== 'string') {
      return { equipmentType: result.equipmentType, confidence: result.confidence ?? 0 };
    }

    const detectedConfidence = mismatch?.params['detectedConfidence'];
    if (typeof detectedConfidence !== 'number') {
      return { equipmentType: detectedEquipment, confidence: 0 };
    }

    return { equipmentType: detectedEquipment, confidence: detectedConfidence };
  }

  private getDimensionColorClass(score: number, thresholdMet: boolean | undefined): string {
    if (thresholdMet === undefined) {
      return this.getScoreColorClass(score);
    }

    if (thresholdMet) {
      return 'score--good';
    }

    return 'score--low';
  }

  private getScoreColorClass(score: number): string {
    if (score >= 70) return 'score--good';
    if (score >= 40) return 'score--medium';
    return 'score--low';
  }

  private formatPercentage(value: number, decimals: number): string {
    return `${value.toFixed(decimals)}%`;
  }

  private formatPassFail(passed: boolean): string {
    return passed ? 'PASS' : 'FAIL';
  }

  private getPassFailColorClass(passed: boolean): string {
    return passed ? 'status--pass' : 'status--fail';
  }

  private buildQualityDetails(result: ClassificationResult): QualityScoreVm[] {
    if (!result.qualityDetails) {
      return [];
    }

    const aiI18n = this.i18ns?.aiValidation;
    const details: QualityScoreVm[] = [];

    if (result.qualityDetails.blur_score !== undefined) {
      const value = Math.round(result.qualityDetails.blur_score * 100);
      details.push({
        label: aiI18n?.sharpness || 'Sharpness',
        value,
        valueDisplay: this.formatPercentage(value, 0),
        colorClass: this.getDimensionColorClass(value, result.qualityDetails.blur_score_threshold_met)
      });
    }

    if (result.qualityDetails.exposure_score !== undefined) {
      const value = Math.round(result.qualityDetails.exposure_score * 100);
      details.push({
        label: aiI18n?.exposure || 'Exposure',
        value,
        valueDisplay: this.formatPercentage(value, 0),
        colorClass: this.getDimensionColorClass(value, result.qualityDetails.exposure_score_threshold_met)
      });
    }

    if (result.qualityDetails.resolution_score !== undefined) {
      const value = Math.round(result.qualityDetails.resolution_score * 100);
      details.push({
        label: aiI18n?.resolution || 'Resolution',
        value,
        valueDisplay: this.formatPercentage(value, 0),
        colorClass: this.getDimensionColorClass(value, result.qualityDetails.resolution_score_threshold_met)
      });
    }

    return details;
  }

  private formatEquipmentType(type: string | undefined): string {
    const aiI18n = this.i18ns?.aiValidation;

    if (!type || type === 'Unknown') {
      return aiI18n?.unknown || 'Unknown';
    }

    // Convert camelCase/PascalCase to Title Case with spaces
    return type
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private extractBoundingBoxLabels(result: ClassificationResult): string[] {
    if (!result.boundingBoxes?.length) {
      return [];
    }

    const uniqueLabels = [...new Set(result.boundingBoxes.map(box => box.label))];
    return uniqueLabels.map(label => this.formatEquipmentType(label));
  }

  private hasValidDetection(result: ClassificationResult): boolean {
    return !!(result.equipmentType && result.equipmentType !== '' && result.equipmentType !== 'Unknown');
  }
}
